import {
  NonFilterParamKeys,
  NonFilterParams,
  ParsedUrlSegmentValue,
  SearchFilterKeys,
  SearchFilters,
  SearchFiltersStateFilters,
  DEFAULT_PAGE_SIZE,
  nonFilterParamKeys,
  searchFilterKeys,
  searchParamKeys,
} from '@cinch-nx/data-search'
import { isValidSegmentFilterKey } from '@retail-common/util-search-create-url'
import {
  convertFilterValueToUrlSegment,
  getDefaultSortingCriteria,
  getOrderedParsedFilterMap,
  truncateSegmentsToQueryParams,
} from '../../utils'

function addNonFilterParamToUrlParams<Key extends NonFilterParamKeys>(
  key: Key,
  value: NonFilterParams[Key],
  urlSearchParams: URLSearchParams,
  shouldUseDataDrivenSortOrderAsDefault?: boolean,
) {
  const defaultSortCriteria = getDefaultSortingCriteria(
    shouldUseDataDrivenSortOrderAsDefault,
  )
  switch (key) {
    case 'pageNumber': {
      if (value !== 1) {
        urlSearchParams.set('pageNumber', `${value}`)
      }
      break
    }
    case 'pageSize': {
      if (value !== DEFAULT_PAGE_SIZE) {
        urlSearchParams.set('pageSize', `${value}`)
      }
      break
    }
    case 'sortingCriteria': {
      if (value !== defaultSortCriteria) {
        urlSearchParams.set('sortingCriteria', `${value}`)
      }
      break
    }
    case 'useMonthly': {
      if (value) {
        urlSearchParams.set('useMonthly', `${value}`)
      }
      break
    }
  }
}
function isNonFilterParamKey(key: string): key is NonFilterParamKeys {
  return nonFilterParamKeys.includes(key as NonFilterParamKeys)
}

function isStringArray(value: number | boolean | string[]): value is string[] {
  return Array.isArray(value)
}

export function processYearFilter(
  key: string,
  value: string[],
  filters: Partial<SearchFiltersStateFilters>,
  urlPathMap: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
): boolean {
  if ((key !== 'fromYear' && key !== 'toYear') || value.length !== 1) {
    return false
  }

  const otherYearKey = key === 'fromYear' ? 'toYear' : 'fromYear'
  const otherYearValue =
    filters[otherYearKey as keyof SearchFiltersStateFilters]

  // If years are different or other year is not set, use query params for years
  if (
    otherYearValue === undefined ||
    !isStringArray(otherYearValue) ||
    otherYearValue.length !== 1 ||
    otherYearValue[0] !== value[0]
  ) {
    urlSearchParams.set(key, value.join(','))
    return true
  }

  // If we reach here, the years are the same
  // Only process this for fromYear to avoid duplication
  if (key === 'fromYear') {
    urlPathMap.set('vehicleYear', {
      searchApiValue: `${value[0]}-year`,
      urlSegmentValue: value[0],
    })
  }

  return true
}

/*
 * A maximum on its own becomes a segment; a min+max pair stays in query params, as processYearFilter
 * does for year. financeType is narrowed to 'any' because the API injects financeType=any when it
 * resolves a -per-month segment, so pcp/hp would read back as a different filter.
 */
export function processPriceFilter(
  key: string,
  value: string[],
  filters: Partial<SearchFiltersStateFilters>,
  urlPathMap: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
  isPriceUrlSegmentEnabled: boolean,
): boolean {
  if (!isPriceUrlSegmentEnabled) {
    return false
  }

  if ((key !== 'fromPrice' && key !== 'toPrice') || value.length !== 1) {
    return false
  }

  const fromPrice = filters.fromPrice ?? []
  const financeType = filters.financeType ?? []

  if (!isStringArray(fromPrice) || !isStringArray(financeType)) {
    return false
  }

  // A minimum is set, so both bounds stay as query params
  if (fromPrice.length > 0) {
    urlSearchParams.set(key, value.join(','))
    return true
  }

  const isMonthlySearch = financeType.length === 1 && financeType[0] === 'any'

  // A monthly search on a financeType a segment cannot represent
  if (financeType.length > 0 && !isMonthlySearch) {
    urlSearchParams.set(key, value.join(','))
    return true
  }

  const priceSegmentKey = isMonthlySearch ? 'monthlyPrice' : 'totalPrice'

  urlPathMap.set(priceSegmentKey, {
    searchApiValue: convertFilterValueToUrlSegment(priceSegmentKey, value[0]),
    urlSegmentValue: value[0],
  })

  return true
}

export function processStoreFilter(
  key: string,
  value: string[],
  allCinchStores: string[],
  urlSearchParams: URLSearchParams,
): boolean {
  if (
    key === 'store' &&
    allCinchStores.length > 0 &&
    value.length === allCinchStores.length &&
    allCinchStores.every((storeValue) => value.includes(storeValue))
  ) {
    urlSearchParams.set(key, 'all-stores')
    return true
  }
  return false
}

export function processStandardFilter(
  key: string,
  value: string[],
  urlPathMap: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
): void {
  if (value.length === 1 && isValidSegmentFilterKey(key)) {
    // single value filters that are allowed in the URL segments should be added to the urlPathMap
    urlPathMap.set(key, {
      searchApiValue: convertFilterValueToUrlSegment(key, value[0]),
      urlSegmentValue: value[0],
    })
  } else if (
    value.length > 1 ||
    (value.length === 1 && !isValidSegmentFilterKey(key))
  ) {
    // multi-value filters and those not allowed in URL segments are added to the search params
    urlSearchParams.set(key, value.join(','))
  }
}

export function handleVehicleYearSpecialCase(
  sortedParsedPath: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
): void {
  // Check if we need to handle vehicleYear special case
  // If vehicleYear is present and we have more than 3 segments, we want to convert it to query params
  if (sortedParsedPath.has('vehicleYear') && sortedParsedPath.size > 3) {
    // Extract the vehicleYear value
    const vehicleYearValue =
      sortedParsedPath.get('vehicleYear')?.urlSegmentValue
    // Add fromYear and toYear as query parameters with the same value
    if (vehicleYearValue) {
      urlSearchParams.set('fromYear', vehicleYearValue)
      urlSearchParams.set('toYear', vehicleYearValue)
      // Remove vehicleYear from sortedParsedPath since we're converting it to query params
      sortedParsedPath.delete('vehicleYear')
    }
  }
}

/*
 * Mirrors handleVehicleYearSpecialCase for price. Price is the lowest-priority segment, so it is
 * the one truncateSegmentsToQueryParams would drop once a fourth segment appears — and a dropped
 * segment loses the filter entirely, because the truncated key is written back as its segment
 * value ("200-per-month"), not as something the filter state understands. Converting it to
 * toPrice here keeps the filter applied.
 */
export function handlePriceSegmentSpecialCase(
  sortedParsedPath: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
): void {
  const priceSegmentKey = (['monthlyPrice', 'totalPrice'] as const).find(
    (segmentKey) => sortedParsedPath.has(segmentKey),
  )

  if (!priceSegmentKey || sortedParsedPath.size <= 3) {
    return
  }

  const priceValue = sortedParsedPath.get(priceSegmentKey)?.urlSegmentValue

  if (priceValue) {
    urlSearchParams.set('toPrice', priceValue)
    sortedParsedPath.delete(priceSegmentKey)
  }
}

export function combineUrlComponents(
  trimmedSortedParsedPath: Map<string, ParsedUrlSegmentValue>,
  urlSearchParams: URLSearchParams,
): string {
  // sort the URL search params to maintain a stable order
  urlSearchParams.sort()

  // combine trimmedSortedParsedPath into a URL
  const urlPathSegments = [...trimmedSortedParsedPath.values()]

  // convert slashes into dashes to prevent segment overloading
  const sanitisedUrlPathSegments = urlPathSegments.map((s) =>
    s.searchApiValue.replace(/\//g, '-').replace(/\[/g, '(').replace(/]/g, ')'),
  )

  // Join the segments...
  let urlPath = sanitisedUrlPathSegments.join('/')

  // if we've got any search params, add them
  if ([...urlSearchParams.values()].length > 0) {
    urlPath += `?${urlSearchParams}`
  }

  return urlPath
}

export function processAllFilters(
  filters: Partial<SearchFiltersStateFilters>,
  allCinchStores: string[],
  shouldUseDataDrivenSortOrderAsDefault?: boolean,
  isPriceUrlSegmentEnabled = false,
): {
  urlPathMap: Map<string, ParsedUrlSegmentValue>
  urlSearchParams: URLSearchParams
} {
  return Object.entries(filters).reduce(
    ({ urlPathMap, urlSearchParams }, [key, value]) => {
      if (isNonFilterParamKey(key)) {
        addNonFilterParamToUrlParams(
          key,
          value as NonFilterParams[NonFilterParamKeys],
          urlSearchParams,
          shouldUseDataDrivenSortOrderAsDefault,
        )
        return { urlPathMap, urlSearchParams }
      }

      if (!isStringArray(value)) {
        console.warn(
          `Unexpected type value for key '${key}', expected string[] but got ${typeof value}`,
        )

        return { urlPathMap, urlSearchParams }
      }

      if (processYearFilter(key, value, filters, urlPathMap, urlSearchParams)) {
        return { urlPathMap, urlSearchParams }
      }

      if (
        processPriceFilter(
          key,
          value,
          filters,
          urlPathMap,
          urlSearchParams,
          isPriceUrlSegmentEnabled,
        )
      ) {
        return { urlPathMap, urlSearchParams }
      }

      if (processStoreFilter(key, value, allCinchStores, urlSearchParams)) {
        return { urlPathMap, urlSearchParams }
      }

      processStandardFilter(key, value, urlPathMap, urlSearchParams)

      return { urlPathMap, urlSearchParams }
    },
    {
      urlPathMap: new Map<string, ParsedUrlSegmentValue>(),
      urlSearchParams: new URLSearchParams(),
    },
  )
}

export function addRemainingParams(
  urlSearchParams: URLSearchParams,
  additionalQueryParams: URLSearchParams,
  filters: Partial<SearchFiltersStateFilters>,
  existingParams: URLSearchParams,
): void {
  // append additionalQueryParams to urlSearchParams
  additionalQueryParams.forEach((_, key) => {
    if (searchFilterKeys.includes(key as SearchFilterKeys)) {
      const searchParamValue = filters[key as keyof SearchFilters]
      if (searchParamValue) {
        urlSearchParams.set(key, searchParamValue.join(','))
      }
    }
  })

  // Add any existing non-filter URL search params
  existingParams.forEach((value, key) => {
    if (!searchParamKeys.includes(key as SearchFilterKeys)) {
      urlSearchParams.set(key, value)
    }
  })
}

/**
 * Maps search filters to a URL string by processing filters into URL path segments and query parameters.
 *
 * @param filters - Partial search filters state containing filter criteria
 * @param allCinchStores - Array of all available Cinch store identifiers
 * @param existingParams - Existing URL search parameters to preserve (defaults to current window location search)
 * @param shouldUseDataDrivenSortOrderAsDefault - Optional flag to use data-driven sort order as default
 * @returns A formatted URL string combining path segments and query parameters
 *
 * @example
 * ```typescript
 * const filters = { make: 'Toyota', model: 'Camry' };
 * const stores = ['store1', 'store2'];
 * const url = mapFiltersToUrl(filters, stores);
 * // Returns: "/toyota/camry?param1=value1"
 * ```
 */
export function mapFiltersToUrl(
  filters: Partial<SearchFiltersStateFilters>,
  allCinchStores: string[],
  existingParams: URLSearchParams = new URLSearchParams(window.location.search),
  shouldUseDataDrivenSortOrderAsDefault?: boolean,
  isPriceUrlSegmentEnabled = false,
): string {
  const { urlPathMap, urlSearchParams } = processAllFilters(
    filters,
    allCinchStores,
    shouldUseDataDrivenSortOrderAsDefault,
    isPriceUrlSegmentEnabled,
  )

  const { orderedParsedFilterMap: sortedParsedPath } =
    getOrderedParsedFilterMap(urlPathMap)

  handleVehicleYearSpecialCase(sortedParsedPath, urlSearchParams)
  handlePriceSegmentSpecialCase(sortedParsedPath, urlSearchParams)

  const {
    truncatedOrderedParsedPath: trimmedSortedParsedPath,
    additionalQueryParams,
  } = truncateSegmentsToQueryParams({
    orderedParsedFilterMap: sortedParsedPath,
    maxAllowedSegments: 3,
  })

  addRemainingParams(
    urlSearchParams,
    additionalQueryParams,
    filters,
    existingParams,
  )

  /*
   * The path already implies financeType=any. Checked against the final path: if the segment was
   * demoted to toPrice, financeType is all that marks the search as monthly and must stay.
   */
  if (
    trimmedSortedParsedPath.has('monthlyPrice') &&
    urlSearchParams.get('financeType') === 'any'
  ) {
    urlSearchParams.delete('financeType')
  }

  return combineUrlComponents(trimmedSortedParsedPath, urlSearchParams)
}
